'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Help, Lock } from '@mui/icons-material';
import {
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Avatar as MuiAvatar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Avatar } from '@prismo-io/design-system';
import { Application, translate } from '@prismo-io/router';
import {
  LanguageCode,
  type LanguageCodeT,
  type UsersT,
} from '@prismo-io/schemas';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type FC, useMemo } from 'react';

type Props = {
  user: Pick<UsersT, 'url' | 'full_name' | 'initials' | 'avatar'>;
  order?: number;
  isCompleted?: boolean;
  disabled?: boolean;
  isPrivate?: boolean;
};

export const ProfileItem: FC<Props> = (props: Props) => {
  const { user, disabled = false, order = 1, isPrivate = false } = props;

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  const pathname = usePathname();

  const baseUrl = useMemo(
    () =>
      translate({
        app: Application.APP,
        path: '/profil/:user/accueil',
        params: {
          user: user.url,
        },
        locale: locale as LanguageCodeT,
      }),
    [locale, user]
  );

  const selected = useMemo(() => {
    return pathname === baseUrl;
  }, [baseUrl, pathname]);

  if (isPrivate) {
    return (
      <MenuItem href={baseUrl} selected={selected} sx={{ order, paddingY: 2 }}>
        <ListItemAvatar sx={{ minWidth: '24px', marginRight: 1 }}>
          <MuiAvatar
            sx={{
              width: 24,
              height: 24,
              paddingX: '1px',
              bgcolor: 'primary.main',
              fontSize: 10,
              fontWeight: 'bold',
            }}
          >
            <Lock sx={{ fontSize: '10px' }} />
          </MuiAvatar>
        </ListItemAvatar>
        <ListItemText>
          <Typography display="inline" fontWeight="bolder">
            <Trans>Profil privé</Trans>
          </Typography>
        </ListItemText>
        <ListItemIcon>
          <Tooltip
            title={t`Ce profil est privé, vous ne pouvez donc pas découvrir ses résultats.`}
          >
            <Help sx={{ fontSize: 16 }} />
          </Tooltip>
        </ListItemIcon>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      component={Link}
      href={baseUrl}
      selected={selected}
      sx={{ order, paddingY: 2 }}
      disabled={disabled}
    >
      <ListItemAvatar sx={{ minWidth: '24px', marginRight: 1 }}>
        <Avatar
          avatar={{
            sx: {
              width: 24,
              height: 24,
              paddingX: '1px',
              bgcolor: 'primary.main',
              fontSize: 10,
              fontWeight: 'bold',
            },
          }}
          fill
          src={user.avatar}
          alt={t`Image de profil de ${user.full_name}`}
          replaceOnError={user.initials}
        />
      </ListItemAvatar>
      <ListItemText>
        <Typography display="inline" fontWeight="bolder">
          {user.full_name}
        </Typography>
      </ListItemText>
    </MenuItem>
  );
};
